/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';

import {
	ConfluencePolicyEnabledStatus,
	type useDataRetentionDetailsGetDetailsQuery as useDataRetentionDetailsGetDetailsQuery$data,
	type useDataRetentionDetailsGetDetailsQueryVariables as useDataRetentionDetailsGetDetailsQuery$variables,
} from './__types__/useDataRetentionDetailsGetDetailsQuery';

export const useDataRetentionDetailsGetDetailsQuery = gql`
	query useDataRetentionDetailsGetDetailsQuery($cloudId: ID!) {
		confluence_dataLifecycleManagementPolicy(cloudId: $cloudId) {
			dataRetentionPolicyDetailsForWorkspace {
				policyEnabledStatus
				disabledOnDate {
					value
				}
			}
		}
	}
`;

/**
 * Note: this hook is used to render FE details about the data retention policy;
 *   We currently handle all errors and fail gracefully, but all behavior will be
 *   strictly enforced on the backend nevertheless.
 */
export const useDataRetentionDetails = () => {
	const { cloudId } = useSessionData();

	const { data, error, loading } = useQuery<
		useDataRetentionDetailsGetDetailsQuery$data,
		useDataRetentionDetailsGetDetailsQuery$variables
	>(useDataRetentionDetailsGetDetailsQuery, {
		skip: !cloudId || !fg('dlp-dlm-data-retention-policy'),
		variables: {
			cloudId,
		},
	});

	if (error) {
		markErrorAsHandled(error);
	}

	const { dataRetentionPolicyDetailsForWorkspace } =
		data?.confluence_dataLifecycleManagementPolicy ?? {};
	const { policyEnabledStatus, disabledOnDate } = dataRetentionPolicyDetailsForWorkspace ?? {};
	const { value: disabledOnEpochString } = disabledOnDate ?? {};

	const isDataRetentionPolicyEnabled =
		policyEnabledStatus === ConfluencePolicyEnabledStatus.ENABLED;
	const disabledOnEpoch =
		!isDataRetentionPolicyEnabled && disabledOnEpochString
			? parseInt(disabledOnEpochString, 10)
			: null;

	return {
		isDataRetentionPolicyEnabled,
		disabledOnEpoch,
		error,
		loading,
	};
};
